/*
  @layer  module
  @name   Fiche produit — barre d'achat fixe en mobile (`<enet-sticky-atc>`)
  @deps   snippets/enet-mod-pdp-sticky-atc.liquid pour le balisage ; assets/enet-mod-pdp.css pour
          l'habillage ; `#ProductSubmitButton-<id>` et `#product-form-<id>` de `buy-buttons.liquid`
          (Dawn) ; `#price-<id>` du bloc prix ; assets/constants.js + assets/pubsub.js (Dawn)
  @i18n   aucune — tout texte affiché est recopié depuis le bouton d'ajout de Dawn

  CE FICHIER NE CONTIENT AUCUNE LOGIQUE D'ACHAT, ET C'EST LA DÉCISION CENTRALE DU COMPOSANT.

  Une barre d'achat fixe se construit spontanément comme un second formulaire : on redéclare un
  `<form>`, un `<input name="id">`, un bouton, et on synchronise. C'est le mauvais chemin, et il
  coûte cher — deux formulaires à tenir d'accord sur la variante, la quantité, les règles de
  quantité, l'état épuisé, le formulaire de destinataire des cartes cadeaux, les erreurs d'ajout.
  Chaque divergence devient un bug silencieux qui ne se voit qu'en mobile.

  Ici il n'y a **qu'un** formulaire, celui de Dawn, et le bouton fixe lui est rattaché par
  l'attribut HTML `form="product-form-<id>"`. C'est du HTML natif : un bouton `submit` associé à un
  formulaire par identifiant déclenche l'événement `submit` de ce formulaire, où qu'il soit dans le
  document. `product-form.js` l'intercepte comme n'importe quelle soumission et fait son ajout en
  AJAX, sans savoir qu'un second bouton existe.

  TROIS CONSÉQUENCES GRATUITES, ET ELLES SONT LA RAISON DU CHOIX.

  1. **La quantité est respectée.** Le sélecteur de quantité est un bloc distinct, hors du
     formulaire, que Dawn rattache déjà par `form="…"`. `new FormData(form)` collecte les champs par
     appartenance au formulaire et non par imbrication : la quantité saisie part avec l'ajout.
  2. **La variante est respectée.** `product-info.js` écrit l'identifiant de variante dans
     l'`<input name="id">` du formulaire à chaque changement. Il n'y a rien à propager.
  3. **La double soumission est déjà bloquée.** `product-form.js` sort si le vrai bouton porte
     `aria-disabled="true"`, ce qu'il pose pendant l'ajout. Un appui sur la barre pendant qu'un ajout
     est en cours ne part pas.

  L'ÉTAT AFFICHÉ EST UN MIROIR, PAS UN CALCUL.

  Le bouton de la barre recopie le contenu et les attributs d'état du vrai bouton, sous
  `MutationObserver`. Il aurait été possible de s'abonner à `variantChange` et de relire le document
  renvoyé par le serveur — c'est ce que fait Dawn — mais cela redéclare ici les règles qui décident
  du libellé et de l'état désactivé. Deux implémentations de la même règle finissent par diverger :
  un cas d'épuisement par règle de quantité corrigé d'un côté et pas de l'autre, et la barre annonce
  « Ajouter au panier » sur un produit indisponible.

  Le miroir n'a pas cette classe de bug par construction. Il suit aussi ce qu'aucun abonnement ne
  donnerait sans code supplémentaire : l'état de chargement — `product-form.js` pose la classe
  `loading` et démasque son `.loading__spinner`, la recopie du contenu embarque le spinner.

  Corollaire : le libellé rendu en Liquid n'est qu'un point de départ, et le bouton part `disabled`.
  Si le miroir ne s'installait pas, la barre resterait inerte plutôt que d'offrir un ajout dont elle
  ne connaît pas l'état.
*/

if (!customElements.get('enet-sticky-atc')) {
  customElements.define(
    'enet-sticky-atc',
    class EnetStickyAtc extends HTMLElement {
      connectedCallback() {
        const id = this.dataset.sectionId;

        this.form = document.getElementById(`product-form-${id}`);
        this.sourceButton = document.getElementById(`ProductSubmitButton-${id}`);
        this.targetButton = this.querySelector('[data-sticky-atc-submit]');
        this.sourcePrice = document.getElementById(`price-${id}`);
        this.targetPrice = this.querySelector('[data-sticky-atc-price]');

        /*
          Le composant se retire lui-même plutôt que de rester en place sans rien faire. Le cas
          n'est pas théorique : un marchand peut retirer le bloc « Boutons d'achat » de la colonne
          d'achat, et il n'y a alors ni formulaire ni bouton à relayer. Une barre morte qui recouvre
          le bas de l'écran serait pire que pas de barre.
        */
        if (!this.form || !this.sourceButton || !this.targetButton) {
          this.remove();
          return;
        }

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.mirror();
        this.watchSource();
        this.watchCtaVisibility();
        this.watchPageEnd();
        this.watchCartErrors();
      }

      disconnectedCallback() {
        this.sourceObserver?.disconnect();
        this.ctaObserver?.disconnect();
        this.pageEndObserver?.disconnect();
        this.cartErrorUnsubscriber?.();
      }

      /* ----------------------------------------------------------------------
         Miroir de l'état
         -------------------------------------------------------------------- */

      mirror() {
        const source = this.sourceButton;
        const target = this.targetButton;

        /*
          Comparaison avant écriture. Le spinner de chargement change de classe pendant l'ajout,
          donc l'observateur se déclenche plusieurs fois par soumission ; réécrire un contenu
          identique reconstruirait les nœuds pour rien à chaque fois.
        */
        if (target.innerHTML !== source.innerHTML) target.innerHTML = source.innerHTML;

        /*
          `disabled` **et** `aria-disabled` : Dawn se sert des deux, et pas pour la même chose.
          `disabled` marque l'indisponibilité durable — épuisé, variante inexistante — et interdit
          la soumission ; `aria-disabled` marque l'ajout en cours, en gardant le bouton focalisable
          pour ne pas éjecter le focus du clavier au milieu de l'action. Ne recopier que le premier
          laisserait la barre cliquable pendant un ajout.

          `aria-haspopup` est le troisième, et il ne change jamais après le chargement :
          `product-form.js` le pose une fois, dans son constructeur, quand la page contient un
          `<cart-drawer>` — ce qui est le cas en mobile, le panneau de panier n'étant en dropdown
          qu'au-delà de 990 px. Sans lui, le bouton fixe n'annoncerait pas qu'il ouvre un panneau
          alors que le vrai le fait, et les deux ne diraient pas la même chose de la même action.
          Recopié ici plutôt que rendu en Liquid : le Liquid ne sait pas quel type de panier la
          boutique a choisi, et cette information vit déjà dans le bouton qu'on reflète.
        */
        for (const attribute of ['disabled', 'aria-disabled', 'aria-haspopup']) {
          const value = source.getAttribute(attribute);
          if (value === null) target.removeAttribute(attribute);
          else target.setAttribute(attribute, value);
        }

        target.classList.toggle('loading', source.classList.contains('loading'));

        this.mirrorPrice();
      }

      /*
        Le prix est remplacé en bloc par un clone, et non recopié en `innerHTML`. Les états du prix
        vivent en **classes sur la racine** `.price` — `price--on-sale`, `price--sold-out`,
        `price--no-compare` — que `innerHTML` laisserait derrière. Un produit passé en promotion par
        changement de variante afficherait alors ses deux montants sans le traitement du prix barré.

        `replaceChildren` sur l'enveloppe et non sur le clone : c'est l'enveloppe qui porte
        `aria-hidden` et le crochet d'habillage, elle doit survivre au remplacement.
      */
      mirrorPrice() {
        if (!this.sourcePrice || !this.targetPrice) return;

        const source = this.sourcePrice.querySelector('.price');
        if (!source) return;

        this.targetPrice.replaceChildren(source.cloneNode(true));
      }

      watchSource() {
        this.sourceObserver = new MutationObserver(() => this.mirror());

        const options = { attributes: true, characterData: true, childList: true, subtree: true };
        this.sourceObserver.observe(this.sourceButton, options);
        if (this.sourcePrice) this.sourceObserver.observe(this.sourcePrice, options);
      }

      /* ----------------------------------------------------------------------
         Conditions d'apparition
         -------------------------------------------------------------------- */

      /*
        LA BARRE N'APPARAÎT QUE QUAND LE VRAI BOUTON A ÉTÉ **DÉPASSÉ**, PAS DÈS QU'IL EST ABSENT DE
        L'ÉCRAN. C'est la règle qui fait tout le comportement, et la nuance n'est pas cosmétique.

        Sur une fiche produit en mobile, le bouton d'ajout est loin sous la ligne de flottaison —
        après le titre, le prix, les accroches et le sélecteur de variante. Un simple « le bouton
        n'est pas visible, donc j'affiche la barre » la ferait donc apparaître **au chargement**, sur
        un écran où le visiteur n'a pas encore vu le produit. C'est le reproche le plus courant fait
        à ce composant, et c'est un défaut de règle, pas de réglage.

        La direction lève l'ambiguïté sans seuil arbitraire. Un bouton hors écran est soit au-dessus
        — donc déjà vu et laissé derrière, la barre a une raison d'être — soit en dessous — donc pas
        encore atteint, et la barre n'a rien à proposer que le défilement n'apporte. Le signe de
        `boundingClientRect.top` distingue les deux cas, et aucune valeur en pixels n'est écrite
        nulle part.

        DEUX PROPRIÉTÉS TOMBENT DE CETTE RÈGLE, ET LA SECONDE EST CELLE QU'ON AURAIT CODÉE À PART.

        Il n'y a **jamais deux appels à l'action à l'écran** : dès que le vrai bouton entre dans le
        cadre, `isIntersecting` passe à vrai et la barre se retire.

        Et la barre **ne peut pas masquer le bouton qu'elle double**. Un bouton qui remonte depuis le
        bas de l'écran passe derrière la barre avant d'être franchement visible — le défaut classique
        du motif, qu'on corrige d'habitude par une marge d'observation de la hauteur de la barre. Ici
        l'état « pas encore atteint » est déjà l'état « barre masquée » : la situation ne se produit
        pas. Rien à mesurer, rien à recalculer au redimensionnement.

        Approximation connue et assumée : l'en-tête collant recouvre le haut du cadre, donc le vrai
        bouton peut être « visible » pour l'observateur tout en étant caché derrière l'en-tête. La
        fenêtre est de la hauteur de l'en-tête, franchie en un geste de défilement. La corriger
        demanderait de faire dépendre ce composant de la hauteur mesurée de l'en-tête, pour un
        bénéfice qui ne se voit pas.
      */
      watchCtaVisibility() {
        this.ctaObserver = new IntersectionObserver(
          ([entry]) => {
            this.ctaPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.apply();
          },
          { threshold: 0 }
        );

        this.ctaObserver.observe(this.sourceButton);
      }

      /*
        LA BARRE SE RETIRE QUAND LE PIED DE PAGE ARRIVE, ET C'EST CE QUI DISPENSE DE RÉSERVER DE LA
        PLACE EN BAS DE PAGE.

        Un élément fixe recouvre le bas du cadre en permanence. Sur du contenu défilant ce n'est pas
        un problème — on continue de défiler et tout finit par passer au-dessus. Sur le **dernier**
        contenu de la page, si : les mentions légales et les moyens de paiement resteraient
        définitivement sous la barre, hors d'atteinte. Du contenu inaccessible est un vrai défaut
        d'accessibilité, pas une gêne esthétique.

        Le remède habituel est un `padding-bottom` sur la page, de la hauteur de la barre. Deux
        raisons de ne pas le prendre ici. Posé en même temps que la barre, il déclenche un saut de
        mise en page à chaque apparition. Posé en permanence, il fait apparaître une bande de fond de
        page **sous** le pied de page, qui porte son propre jeu de couleurs — la couture se voit et
        se lit comme un bug.

        Retirer la barre à l'arrivée du pied de page règle les deux d'un coup, et se lit comme une
        intention : on est arrivé au bout, l'achat rapide n'a plus lieu d'être. Aucun seuil ni
        hauteur à mesurer non plus — le premier pixel de pied de page suffit, ce qui garantit qu'il
        n'est jamais recouvert, même partiellement.

        Sans groupe de pied de page repérable, l'observateur n'est pas posé : la barre reste alors
        visible jusqu'en bas. C'est la dégradation acceptable, le reste du composant fonctionnant.
      */
      watchPageEnd() {
        const pageEnd = document.querySelector('.shopify-section-group-footer-group');
        if (!pageEnd) return;

        this.pageEndObserver = new IntersectionObserver(
          ([entry]) => {
            this.pageEndReached = entry.isIntersecting;
            this.apply();
          },
          { threshold: 0 }
        );

        this.pageEndObserver.observe(pageEnd);
      }

      /*
        `inert` en plus de la transition CSS, et les deux sont nécessaires.

        La feuille masque la barre par `visibility`, ce qui la retire déjà de l'ordre de tabulation
        et de l'arbre d'accessibilité — mais seulement **à la fin** de la transition, puisque c'est
        ce délai qui laisse la barre glisser hors du cadre avant de disparaître. `inert` coupe
        immédiatement le focus et les événements de pointeur, donc rien n'est actionnable pendant les
        deux cents millisecondes de sortie.

        L'attribut est posé et retiré ensemble avec `data-state` : un seul point de vérité pour
        « visible », ce qui interdit l'état où la barre se voit sans être utilisable.
      */
      apply() {
        const visible = Boolean(this.ctaPassed) && !this.pageEndReached;

        this.dataset.state = visible ? 'visible' : 'hidden';
        this.toggleAttribute('inert', !visible);
      }

      /* ----------------------------------------------------------------------
         Erreurs d'ajout
         -------------------------------------------------------------------- */

      /*
        L'ÉCHEC D'UN AJOUT EST LE SEUL TROU QUE LE RATTACHEMENT NATIF NE COMBLE PAS.

        `product-form.js` écrit le message d'erreur dans `.product-form__error-message-wrapper`, qui
        vit **dans la colonne d'achat** — donc hors écran, très exactement dans la situation où la
        barre est visible. Le message est un `role="alert"`, il est donc annoncé aux lecteurs
        d'écran ; mais un visiteur voyant appuierait sur un bouton qui redevient normal sans que rien
        n'apparaisse, et conclurait que l'ajout a fonctionné.

        On ramène donc le formulaire dans le cadre. Pas de message dupliqué dans la barre : deux
        endroits où la même erreur peut s'afficher, c'est deux endroits à tenir à jour, et le
        `role="alert"` de Dawn annoncerait alors deux fois.

        `block: 'center'` plutôt que le haut du cadre : le message est au-dessus du bouton, et le
        haut du cadre est justement ce que l'en-tête collant recouvre.
      */
      watchCartErrors() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        /*
          Argument lu et non déstructuré. `product-form.js` est aujourd'hui le seul à publier cet
          événement, et il passe toujours un objet ; mais rien n'empêche une app ou une version
          ultérieure de Dawn de le publier sans charge utile, et une déstructuration de `undefined`
          lèverait alors une exception dans un abonné qui n'était même pas concerné.
        */
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
          if (event?.source !== 'product-form') return;

          this.form.scrollIntoView({
            behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            block: 'center',
          });
        });
      }
    }
  );
}
